import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useForm, zodResolver } from "@mantine/form";

import { isEmpty } from "lodash";

import { PatientContext } from "@/contexts/patient";
import { CPTCodeDrawerAction } from "@/encounters/constants/cpt-code";
import useEncounterProceduresUnsavedChanges from "@/encounters/hooks/useEncounterProceduresUnsavedChanges";
import { Status } from "@/encounters/zod-schemas/encounters";
import { useAppDispatch } from "@/store";
import {
  selectAllActiveProcedures,
  selectTargetProcedure,
  setProcedures,
} from "@/store/slices/procedures";
import ProceduresForm, { ProceduresFormType } from "@/zod-schemas/procedures";

import EncounterProceduresAccordion from "./components/EncounterProceduresAccordion";
import EncounterProceduresDrawer from "./components/EncounterProceduresDrawer";
import EncounterProceduresNoData from "./components/EncounterProceduresNoData";
import { fetchProceduresQuestionnaireResponse } from "./services/questionnaire-response";
import { parseProcedureQuestionnaireResponse, toProceduresFormValues } from "./utils/procedure";

type EncounterProceduresProps<T> = {
  drawerName: T;
  submitTrigger: number;
  encounter: Encounter;
  lastAction?: CPTCodeDrawerAction;
  isAmendment: boolean;
  register: (id: T) => {
    opened: boolean;
    onClose: () => void;
    stackId: T;
  };
  closeAll: () => void;
  open: (id: T) => void;
  mainQuestionnaireUrl: string;
  isAutoSave?: boolean;
};

const EncounterProcedures = <T,>({
  drawerName,
  submitTrigger,
  encounter,
  lastAction,
  isAmendment,
  register,
  closeAll,
  open,
  mainQuestionnaireUrl,
}: EncounterProceduresProps<T>) => {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { patient } = useContext(PatientContext);
  const isEncounterActive =
    encounter.status !== Status.Finished && encounter.status !== Status.Cancelled;

  const targetCptCode = useSelector(selectTargetProcedure);
  const procedures = useSelector(selectAllActiveProcedures);

  const form = useForm<ProceduresFormType>({
    validate: zodResolver(ProceduresForm),
  });

  const { markProceduresListSaved, markDrawerFormSaved } = useEncounterProceduresUnsavedChanges(
    form,
    targetCptCode,
    encounter.id,
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      const questionnaireResponses = await fetchProceduresQuestionnaireResponse(
        medplum,
        patient?.id,
        encounter.id,
      );
      dispatch(setProcedures(parseProcedureQuestionnaireResponse(questionnaireResponses)));
      markProceduresListSaved();
    };

    fetchInitialData();
  }, [dispatch, encounter.id, markProceduresListSaved, medplum, patient?.id]);

  useEffect(() => {
    return () => {
      dispatch(setProcedures([]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (targetCptCode) {
      form.setValues(toProceduresFormValues(targetCptCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCptCode]);

  const isCptCodeDataEmpty = isEmpty(procedures);

  return (
    <>
      <EncounterProceduresDrawer
        form={form}
        closeAll={closeAll}
        register={register}
        encounter={encounter}
        drawerName={drawerName}
        lastAction={lastAction}
        submitTrigger={submitTrigger}
        targetCptCode={targetCptCode}
        markDrawerFormSaved={markDrawerFormSaved}
        mainQuestionnaireUrl={mainQuestionnaireUrl}
        markProceduresListSaved={markProceduresListSaved}
      />
      {isCptCodeDataEmpty ? (
        <EncounterProceduresNoData
          open={open}
          drawerName={drawerName}
          isAmendment={isAmendment}
          isEncounterActive={isEncounterActive}
        />
      ) : (
        <EncounterProceduresAccordion
          form={form}
          open={open}
          closeAll={closeAll}
          drawerName={drawerName}
          isAmendment={isAmendment}
          isEncounterActive={isEncounterActive}
          markDrawerFormSaved={markDrawerFormSaved}
          markProceduresListSaved={markProceduresListSaved}
        />
      )}
    </>
  );
};

export default EncounterProcedures;
